import { useState, useEffect } from "react";
import { Table, AdvancedFilterColumn } from "@velocity-ehs/component-library";
import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import { SortDirection } from "../../supportFunctions/graphql/Queries";
import { Columns } from "./EhsConfigurationsColumns";
import { useGetEhsConfigurations } from "../../../hooks/useGetEhsConfigurations";
import { EhsConfiguration } from "./Utils";
import useEhsConfigurationMutations from "../../../hooks/useEhsConfigurationMutations";

const defaultParams = {
  first: 0,
  rows: 20,
  page: 0,
  sortField: "IsActive",
  sortOrder: SortDirection.DESC,
  filters: {
    language: {
      value: [],
    },
  },
};

type LanguageConfigTableProps = {
  languageConfigTypes: any[];
  rowActions: (data: EhsConfiguration) => any[];
  setSelected: (data: any) => void;
};

const LanguageConfigTable = ({
  languageConfigTypes,
  rowActions,
  setSelected,
}: LanguageConfigTableProps) => {
  const { t } = useTranslation();
  const [lazyParams, setLazyParams] = useState(defaultParams);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [configTypeIds, setConfigTypeIds] = useState([]);

  const { ehsConfigurations, pagination, isFetching } = useGetEhsConfigurations(
    lazyParams,
    configTypeIds.length ? configTypeIds : undefined
  );

  const { mutations } = useEhsConfigurationMutations();

  const onApplyFilter = (value: any) => {
    const ids = [];
    value.forEach((lang) => {
      const type = languageConfigTypes.find((type) => type.name === lang);
      if (type) ids.push(type.id);
    });
    setConfigTypeIds(ids);
    setFilterIconStatus(setLazyParams, ids, ids.length === 0);
  };

  useEffect(() => {
    const ids = languageConfigTypes.map((type) => type.id);
    if (languageConfigTypes.length && !configTypeIds.length) {
      setConfigTypeIds(ids);
    }
  }, [languageConfigTypes, configTypeIds, setConfigTypeIds]);

  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination]);

  function getTableColumns() {
    return Columns.languages.map((column) => {
      if (column.filterable) {
        return AdvancedFilterColumn({
          field: column.field,
          filterField: column.filterField,
          header: t(column.header),
          options: column.options,
          onApply: onApplyFilter,
          body: column.body,
          filterParams: lazyParams.filters,
        });
      }
      return (
        <Column
          key={column.field}
          field={column.field}
          header={t(column.header)}
          sortable={column.sortable}
          body={column.body}
        />
      );
    });
  }

  return (
    <Table
      data={ehsConfigurations}
      params={lazyParams}
      totalRecords={totalRecords}
      selectionMode="single"
      onRowSelect={(e) => setSelected(e)}
      rowActions={rowActions}
      isLoading={isFetching || mutations.isLoading}
      onSort={setLazyParams}
      onPage={setLazyParams}
    >
      {getTableColumns()}
    </Table>
  );
};

function setFilterIconStatus(
  setLazyParams: Function,
  ids: string[],
  override: boolean
) {
  setLazyParams((previous) => ({
    ...previous,
    filters: {
      ...previous.filters,
      language: {
        value: override ? [] : ids,
      },
    },
  }));
}

export default LanguageConfigTable;
